import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Users, Mail } from "lucide-react";

const borderStyle = { borderColor: "var(--rs-border)" };

const downloadCsv = async (kind) => {
  try {
    const res = await api.get(`/admin/export/${kind}.csv`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ramshack_${kind}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Export failed");
  }
};

const AdminCustomers = () => {
  const [customers, setCustomers] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [carts, setCarts] = useState([]);
  const [tab, setTab] = useState("customers");

  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
    api.get("/admin/newsletter/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
    api.get("/admin/abandoned-carts").then((r) => setCarts(r.data)).catch(() => {});
  }, []);

  const runRecovery = async () => {
    try {
      const { data } = await api.post("/admin/abandoned-carts/run", { hours: 4 });
      toast.success(`Recovery run: ${data.sent} email(s) sent (${data.eligible} eligible)`);
      api.get("/admin/abandoned-carts").then((r) => setCarts(r.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recovery run failed");
    }
  };

  const cartStatusColor = { open: "#555", recovery_sent: "#8a6d1a", converted: "#2f6f4f", abandoned: "var(--rs-oxblood)" };

  return (
    <div data-testid="admin-customers">
      <div className="overline mb-3">People of the house</div>
      <h1 className="font-serif-display text-5xl text-bone mb-10">Customers.</h1>

      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div className="flex gap-6 border-b" style={borderStyle}>
          {[
            { id: "customers", label: `Customers (${customers?.length ?? "…"})`, icon: Users },
            { id: "subscribers", label: `Subscribers (${subscribers.length})`, icon: Mail },
            { id: "carts", label: `Carts (${carts.length})` },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`customers-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`pb-3 text-xs uppercase tracking-[0.22em] ${tab === t.id ? "text-bone border-b-2" : "text-muted-bone hover:text-bone"}`}
              style={tab === t.id ? { borderColor: "var(--rs-oxblood)" } : {}}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          data-testid="export-customers-btn"
          onClick={() => downloadCsv("customers")}
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-bone hover:text-bone"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {tab === "customers" && (
        customers === null ? (
          <div className="text-muted-bone">Loading…</div>
        ) : customers.length === 0 ? (
          <div className="border p-16 text-center text-muted-bone" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
            No customers yet.
          </div>
        ) : (
          <div className="border overflow-x-auto" style={borderStyle}>
            <table className="w-full min-w-[820px]">
              <thead>
                <tr className="text-xs uppercase tracking-[0.22em] text-muted-bone">
                  <th className="text-left px-6 py-4">Customer</th>
                  <th className="text-left px-6 py-4">Provider</th>
                  <th className="text-left px-6 py-4">Joined</th>
                  <th className="text-left px-6 py-4">Ship-to</th>
                  <th className="text-right px-6 py-4">Orders</th>
                  <th className="text-right px-6 py-4">Lifetime spend</th>
                  <th className="text-left px-6 py-4">Last order</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c, i) => (
                  <tr key={c.email + i} data-testid={`customer-row-${i}`} className="border-t text-sm" style={borderStyle}>
                    <td className="px-6 py-4">
                      <div className="text-bone">{c.name}</div>
                      <div className="text-xs text-muted-bone">{c.email}</div>
                    </td>
                    <td className="px-6 py-4 text-muted-bone text-xs uppercase tracking-widest">{c.auth_provider}</td>
                    <td className="px-6 py-4 text-muted-bone">{c.created_at?.slice(0, 10) || "—"}</td>
                    <td className="px-6 py-4 text-muted-bone text-xs">
                      {c.ship_city || c.ship_state ? (
                        <>
                          <div className="text-bone">{[c.ship_city, c.ship_state].filter(Boolean).join(", ")}</div>
                          <div>{[c.ship_postal_code, c.ship_country].filter(Boolean).join(" · ")}</div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-bone">{c.orders_count}</td>
                    <td className="px-6 py-4 text-right text-bone">${(c.total_spent || 0).toFixed(2)}</td>
                    <td className="px-6 py-4 text-muted-bone">{c.last_order_at?.slice(0, 10) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "subscribers" && (
        subscribers.length === 0 ? (
          <div className="border p-16 text-center text-muted-bone" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
            No memoirs subscribers yet.
          </div>
        ) : (
          <div className="border" style={borderStyle}>
            <table className="w-full">
              <thead>
                <tr className="text-xs uppercase tracking-[0.22em] text-muted-bone">
                  <th className="text-left px-6 py-4">Email</th>
                  <th className="text-left px-6 py-4">Status</th>
                  <th className="text-left px-6 py-4">Subscribed</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((s) => (
                  <tr key={s.email} className="border-t text-sm" style={borderStyle}>
                    <td className="px-6 py-4 text-bone">{s.email}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-[10px] uppercase tracking-widest text-bone"
                        style={{ background: s.status === "subscribed" ? "#2f6f4f" : "#555" }}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-muted-bone">{s.subscribed_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "carts" && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              data-testid="run-recovery-btn"
              onClick={runRecovery}
              className="px-6 py-3 uppercase text-xs tracking-[0.22em] text-bone hover:opacity-90 transition"
              style={{ background: "var(--rs-oxblood)" }}
            >
              Run abandoned-cart recovery now
            </button>
          </div>
          {carts.length === 0 ? (
            <div className="border p-16 text-center text-muted-bone" style={{ ...borderStyle, background: "var(--rs-surface)" }}>
              No tracked carts yet.
            </div>
          ) : (
            <div className="border overflow-x-auto" style={borderStyle}>
              <table className="w-full min-w-[560px]">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.22em] text-muted-bone">
                    <th className="text-left px-6 py-4">Email</th>
                    <th className="text-left px-6 py-4">Items</th>
                    <th className="text-right px-6 py-4">Value</th>
                    <th className="text-left px-6 py-4">Status</th>
                    <th className="text-left px-6 py-4">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {carts.map((c) => (
                    <tr key={c.cart_id} className="border-t text-sm" style={borderStyle}>
                      <td className="px-6 py-4 text-bone">{c.email || <span className="text-muted-bone">anonymous</span>}</td>
                      <td className="px-6 py-4 text-muted-bone">{(c.items || []).length}</td>
                      <td className="px-6 py-4 text-right text-bone">${(c.subtotal || 0).toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-[10px] uppercase tracking-widest text-bone"
                          style={{ background: cartStatusColor[c.status] || "#555" }}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-muted-bone">{c.updated_at?.slice(0, 16).replace("T", " ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
